import { RESPONSE_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { redirectIfRequired, throwCustomErrorIfProvided } from '../Middleware/Middleware';
import { LegacyFetchErrorFormat, NewFetchErrorFormat } from '@/types/types';
import { FetchError } from '../FetchError/FetchError';
import { getJson, isBadResponse } from '@/utils/fp';
import { isObject } from '@/types/typeGuards';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import * as Sentry from '@sentry/nextjs';

export interface Config {
    disableRedirects?: boolean;
    cache?: RequestCache;
    routerPush?: ReturnType<typeof useRouter>['push'];
    customErrorPhraseId?: {
        401?: number;
        403?: number;
        404?: number;
    };
}

interface RequestOptions {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    endpoint?: string;
    body?: XMLHttpRequestBodyInit;
    cache?: RequestCache;
}

const DEFAULT_REQUEST_OPTIONS: RequestInit = {
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
    credentials: 'include',
};

export const isLegacyErrorFormat = (d: unknown): d is LegacyFetchErrorFormat =>
    isObject(d) && 'message' in d;

export const isNewErrorFormat = (d: unknown): d is NewFetchErrorFormat =>
    isObject(d) && 'errors' in d;

export const getErrorPhraseIds = (resp: unknown): number[] | null => {
    if (isNewErrorFormat(resp)) {
        const filteredErrorPhraseIds = resp.errors.filter((err) => !!err.phraseId) as {
            phraseId: number;
        }[];
        return filteredErrorPhraseIds.map((error) => error.phraseId);
    }
    if (isLegacyErrorFormat(resp) && isObject(resp.message)) {
        return [resp.message?.phraseId];
    }
    return null;
};

export const makeFetcher = (
    baseURL: string,
    defaultRequestOptions?: RequestInit,
    config?: Config,
) => {
    defaultRequestOptions = defaultRequestOptions || DEFAULT_REQUEST_OPTIONS;

    const makeRequest = (requestOptions: RequestOptions) => {
        const { method, endpoint, body, cache } = requestOptions;

        let fetchOptions: RequestInit = {
            method: method,
            ...defaultRequestOptions,
        };

        if (cache) {
            fetchOptions = {
                ...fetchOptions,
                cache,
            };
        }

        if (method !== 'GET') {
            fetchOptions = {
                ...fetchOptions,
                body,
            };
        }

        return fetch(`${baseURL}${endpoint ?? ''}`, fetchOptions);
    };

    const handleRequest = async (
        requestOptions: RequestOptions,
        configOverride: Config = {},
    ): Promise<Response> => {
        try {
            const cache = configOverride.cache;
            const configOptions = { ...config, ...configOverride };
            if (cache) {
                requestOptions.cache = cache;
            }
            const res = await makeRequest(requestOptions);
            return isBadResponse(res)
                ? getJson(res).then((json) => {
                      const errorPhraseIds = getErrorPhraseIds(json);
                      redirectIfRequired(res.status, configOptions, errorPhraseIds);

                      if (isLegacyErrorFormat(json) || isNewErrorFormat(json)) {
                          throwCustomErrorIfProvided(res, configOptions);
                          throw new FetchError(json, res);
                      } else {
                          throw new FetchError(String(RESPONSE_ERROR_PHRASES.UNKNOWN), res);
                      }
                  })
                : res;
        } catch (err) {
            if (err instanceof FetchError) {
                throw err;
            } else {
                Sentry.captureException(err, {
                    extra: { requestOptions, originalError: err },
                });
                throw err;
            }
        }
    };

    return {
        get: (endpoint?: string, configOverride?: Config) =>
            handleRequest({ endpoint, method: 'GET' }, configOverride),
        post: (endpoint?: string, body?: XMLHttpRequestBodyInit, configOverride?: Config) =>
            handleRequest({ endpoint, method: 'POST', body }, configOverride),
        put: (endpoint?: string, body?: XMLHttpRequestBodyInit, configOverride?: Config) =>
            handleRequest({ endpoint, method: 'PUT', body }, configOverride),
        patch: (endpoint?: string, body?: XMLHttpRequestBodyInit, configOverride?: Config) =>
            handleRequest({ endpoint, method: 'PATCH', body }, configOverride),
        delete: (endpoint?: string, configOverride?: Config) =>
            handleRequest({ endpoint, method: 'DELETE' }, configOverride),
    };
};
